<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useLotteryStore } from '../stores/lottery.js'
import { useOrgStore } from '../stores/orgStore.js'
import { usePermissions } from '../utils/permissions.js'
import { awaitScopedContextReady } from '../utils/contextReady.js'
import LotteryCard from '../components/LotteryCard.vue'
import LotterySkeletonCard from '../components/LotterySkeletonCard.vue'
import CreateLotteryForm from '../components/CreateLotteryForm.vue'

const router = useRouter()
const authStore = useAuthStore()
const lotteryStore = useLotteryStore()
const orgStore = useOrgStore()
const { can, isAdmin, isManager } = usePermissions(authStore, orgStore)
const showCreateLottery = ref(false)

const canManage = computed(() => can.createLottery.value)
const visibleLotteries = computed(() => lotteryStore.lotteries || [])

const getLotteryId = (lottery) => lottery?.id ?? lottery?.lottery_id

const openCreateLottery = () => {
  showCreateLottery.value = true
}

const closeCreateLottery = () => {
  showCreateLottery.value = false
}

const handleLotteryCreated = async (lottery) => {
  showCreateLottery.value = false
  await lotteryStore.fetchLotteries()
  const createdId = getLotteryId(lottery)
  const existsInScopedList = (lotteryStore.lotteries || []).some(
    (item) => String(getLotteryId(item)) === String(createdId),
  )
  if (createdId && existsInScopedList) {
    router.push(`/lottery/${createdId}`)
  }
}

const fetchScopedLotteries = async () => {
  const ready = await waitForContextReady()
  if (!ready) return
  if (!orgStore.activeSubOrgId) return
  await lotteryStore.fetchLotteries()
}

const waitForContextReady = async () => {
  return await awaitScopedContextReady(authStore, orgStore)
}

onMounted(async () => {
  const ready = await waitForContextReady()
  if (!ready || !orgStore.activeSubOrgId) return
  await lotteryStore.fetchLotteries()
})

watch(
  () => [orgStore.activeOrgId, orgStore.activeSubOrgId],
  async ([orgId, subOrgId], [previousOrgId, previousSubOrgId]) => {
    if (orgId === previousOrgId && subOrgId === previousSubOrgId) return
    if (!authStore.contextReady) return
    if (!subOrgId) return
    await fetchScopedLotteries()
  },
  { immediate: false },
)

</script>

<template>
  <div class="dashboard-container">
    <header class="dashboard-header">
      <h1>Lotteries</h1>
      <div class="header-actions action-bar sticky-action-bar">
        <button v-if="canManage" @click="openCreateLottery" class="primary-btn">Create lottery</button>
      </div>
    </header>

    <div v-if="lotteryStore.loading" class="lotteries-grid loading-grid">
      <LotterySkeletonCard v-for="n in 6" :key="n" />
    </div>

    <div v-else-if="lotteryStore.error" class="error">
      {{ lotteryStore.error }}
    </div>

    <div v-else-if="visibleLotteries.length > 0" class="lotteries-grid">
      <LotteryCard
        v-for="lottery in visibleLotteries"
        :key="lottery.id ?? lottery.lottery_id"
        :lottery="lottery"
      />
    </div>

    <div v-else class="empty-state">
      <h2>No lotteries yet</h2>
      <p v-if="isAdmin">Create your first lottery, then add users and managers.</p>
      <p v-else-if="isManager">Create your first lottery and assign users/managers.</p>
      <p v-else>No lotteries are available for your account yet. Contact your manager/admin.</p>

      <div v-if="isAdmin" class="cta-row">
        <button class="primary-btn" @click="openCreateLottery">Create lottery</button>
        <button class="secondary-btn" @click="router.push('/users')">User administration</button>
      </div>

      <div v-else-if="isManager" class="cta-row">
        <button class="primary-btn" @click="openCreateLottery">Create lottery</button>
        <button class="secondary-btn" @click="router.push('/users')">Create users</button>
      </div>
    </div>

    <CreateLotteryForm
      v-if="showCreateLottery"
      @created="handleLotteryCreated"
      @cancel="closeCreateLottery"
    />
  </div>
</template>

<style scoped>
.dashboard-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

h1 {
  color: #111827;
}

.primary-btn,
.secondary-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.secondary-btn {
  background: #6b7280;
  color: #fff;
}

.lotteries-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
}

.error {
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
}

.error {
  background-color: #ffebee;
  color: #c62828;
}

.empty-state {
  padding: 1.5rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
}

.empty-state h2 {
  margin-top: 0;
  color: #111827;
}

.empty-state p {
  color: #374151;
}

.cta-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
}
</style>

